import React, {useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, flex, flexDirection, mb, ml, p, ph, pv, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/components/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";
interface IProps {
    offer: IMapOfferSidebar;
    latestQuery: ILatestQuery;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const headerRef = useRef<HTMLDivElement>(null);
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), props.latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);
    const propertyList = usePropertyList(props.offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);

    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;
    const theme = useTheme();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <React.Suspense fallback={<Loader />}>
            <div css={wrapper}>
                <div css={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={props.offer.vendor.logo?.v_log_120x90} />
                    <MapOfferExpandablePanelOfferSwitcher offerId={props.offer.id} />
                </div>
                <div css={contentStyle}>
                    <div css={infoBoxWrapper}>
                        <Image alt={props.offer.name} src={props.offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />
                        <div css={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" css={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(props.offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>
                            <Text>{props.offer.name}</Text>
                            {!!props.offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb="1.2rem" strong>
                                    <Highlight>
                                        {`od ${numberFormat(props.offer?.stats?.ranges_price_m2_min ?? 0)}`}{" "}
                                        {getCurrency({country: props.offer.region.country})}/m<sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}
                            <Text variant="info_txt_2">{props.offer.address}</Text>
                            {!!props.offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${props.offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>
                    <div css={tilesHeaderWrapper} ref={headerRef}>
                        <div css={headerButtonContainer}>
                            <StyledButton
                                isActive={props.propertyListType === PropertyListType.TILES}
                                variant="none_secondary"
                                onClick={() => props.onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                            >
                                Rzuty
                            </StyledButton>
                            <StyledButton
                                isActive={props.propertyListType === PropertyListType.TABLE}
                                variant="none_secondary"
                                onClick={() => props.onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                            >
                                Lista
                            </StyledButton>
                        </div>
                        <div css={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" css={ml(1)} onClick={handlers.openSort} />
                        </div>
                    </div>
                    {props.propertyListType === PropertyListType.TABLE && (
                        <div css={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={props.offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleDivClick={props.handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground={theme.colors.gray[100]}
                            />
                        </div>
                    )}
                    {props.propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={props.offer as unknown as IOfferDetail}
                            css={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => props.handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                        />
                    )}
                    {pageCount > 0 && (
                        <PaginationWithList
                            iconPrev={<ChevronLeftIcon />}
                            iconNext={<ChevronRightIcon />}
                            onChangePageClick={handlers.handlePageChange}
                            requestState={requestState}
                            pageCount={pageCount}
                            currentPage={page.currentPage}
                            multiNumbersInside
                            size={isMobile ? PaginationSize.XS : PaginationSize.SM}
                            css={mb(2)}
                        />
                    )}
                </div>
            </div>
            <PropertyListFiltersModal isOpen={isFiltersModalOpen && !!isMobile} handleClose={handlers.closeFilters} handleConfirm={handlers.saveFilters} />
            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </React.Suspense>
    );
};

const wrapper = css`
    ${w100};
`;

const headerStyle = css`
    ${flex("center", "space-between")};
    background-color: #fff;
    ${ph(1.5)};
`;

const modalPropertyList = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
`;

const tableWrapper = (theme: Theme) => css`
    width: 100vw;
    margin-left: -${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 100%;
        margin-left: initial;
    }
`;

const tilesHeaderWrapper = css`
    ${flex("center", "space-between")};
    ${w100};
`;

interface IStyledButtonProps {
    isActive: boolean;
}

const StyledButton = styled(Button)<IStyledButtonProps>`
    ${p(2, 1)};
    ${({isActive, theme}) =>
        !isActive &&
        css`
            color: ${theme.colors.gray[600]};
            fill: ${theme.colors.gray[600]};
        `}

    :hover {
        color: ${({theme}) => theme.colors.secondary};
        fill: ${({theme}) => theme.colors.secondary};
    }
`;

const headerButtonContainer = css`
    ${flex("center")}
    gap: ${calculateRemSize(2)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${ph(1.5)};
    max-height: calc(100dvh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
`;

const infoBoxWrapper = css`
    ${flex("flex-start")};
    gap: ${calculateRemSize(1)};
    ${pv(2)};
`;

const infoBoxContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(0.5)};
`;

const constructionDatePill = (theme: Theme) => css`
    ${flex("center")};
    ${p(0.5)};
    gap: ${calculateRemSize(0.5)};
    ${borderRadius(2)};
    background-color: ${theme.colors.primary};
`;
import React, {useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, flex, flexDirection, mb, ml, p, ph, pv, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/components/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";
interface IProps {
    offer: IMapOfferSidebar;
    latestQuery: ILatestQuery;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const headerRef = useRef<HTMLDivElement>(null);
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), props.latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);
    const propertyList = usePropertyList(props.offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);

    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;
    const theme = useTheme();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <React.Suspense fallback={<Loader />}>
            <div css={wrapper}>
                <div css={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={props.offer.vendor.logo?.v_log_120x90} />
                    <MapOfferExpandablePanelOfferSwitcher offerId={props.offer.id} />
                </div>
                <div css={contentStyle}>
                    <div css={infoBoxWrapper}>
                        <Image alt={props.offer.name} src={props.offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />
                        <div css={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" css={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(props.offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>
                            <Text>{props.offer.name}</Text>
                            {!!props.offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb="1.2rem" strong>
                                    <Highlight>
                                        {`od ${numberFormat(props.offer?.stats?.ranges_price_m2_min ?? 0)}`}{" "}
                                        {getCurrency({country: props.offer.region.country})}/m<sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}
                            <Text variant="info_txt_2">{props.offer.address}</Text>
                            {!!props.offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${props.offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>
                    <div css={tilesHeaderWrapper} ref={headerRef}>
                        <div css={headerButtonContainer}>
                            <StyledButton
                                isActive={props.propertyListType === PropertyListType.TILES}
                                variant="none_secondary"
                                onClick={() => props.onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                            >
                                Rzuty
                            </StyledButton>
                            <StyledButton
                                isActive={props.propertyListType === PropertyListType.TABLE}
                                variant="none_secondary"
                                onClick={() => props.onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                            >
                                Lista
                            </StyledButton>
                        </div>
                        <div css={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" css={ml(1)} onClick={handlers.openSort} />
                        </div>
                    </div>
                    {props.propertyListType === PropertyListType.TABLE && (
                        <div css={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={props.offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleDivClick={props.handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground={theme.colors.gray[100]}
                            />
                        </div>
                    )}
                    {props.propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={props.offer as unknown as IOfferDetail}
                            css={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => props.handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                        />
                    )}
                    {pageCount > 0 && (
                        <PaginationWithList
                            iconPrev={<ChevronLeftIcon />}
                            iconNext={<ChevronRightIcon />}
                            onChangePageClick={handlers.handlePageChange}
                            requestState={requestState}
                            pageCount={pageCount}
                            currentPage={page.currentPage}
                            multiNumbersInside
                            size={isMobile ? PaginationSize.XS : PaginationSize.SM}
                            css={mb(2)}
                        />
                    )}
                </div>
            </div>
            <PropertyListFiltersModal isOpen={isFiltersModalOpen && !!isMobile} handleClose={handlers.closeFilters} handleConfirm={handlers.saveFilters} />
            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </React.Suspense>
    );
};

const wrapper = css`
    ${w100};
`;

const headerStyle = css`
    ${flex("center", "space-between")};
    background-color: #fff;
    ${ph(1.5)};
`;

const modalPropertyList = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
`;

const tableWrapper = (theme: Theme) => css`
    width: 100vw;
    margin-left: -${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 100%;
        margin-left: initial;
    }
`;

const tilesHeaderWrapper = css`
    ${flex("center", "space-between")};
    ${w100};
`;

interface IStyledButtonProps {
    isActive: boolean;
}

const StyledButton = styled(Button)<IStyledButtonProps>`
    ${p(2, 1)};
    ${({isActive, theme}) =>
        !isActive &&
        css`
            color: ${theme.colors.gray[600]};
            fill: ${theme.colors.gray[600]};
        `}

    :hover {
        color: ${({theme}) => theme.colors.secondary};
        fill: ${({theme}) => theme.colors.secondary};
    }
`;

const headerButtonContainer = css`
    ${flex("center")}
    gap: ${calculateRemSize(2)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${ph(1.5)};
    max-height: calc(100dvh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
`;

const infoBoxWrapper = css`
    ${flex("flex-start")};
    gap: ${calculateRemSize(1)};
    ${pv(2)};
`;

const infoBoxContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(0.5)};
`;

const constructionDatePill = (theme: Theme) => css`
    ${flex("center")};
    ${p(0.5)};
    gap: ${calculateRemSize(0.5)};
    ${borderRadius(2)};
    background-color: ${theme.colors.primary};
`;
